import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { useMutation } from "convex/react";
import { AtSign, Bot, CalendarClock, LoaderCircle, Mic, Paperclip, Send, Square, X } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { errorMessage, formatBytes, idempotencyKey } from "./format";
import type { Conversation, Participant } from "./types";
import { appendTranscript, formatRecordingTime, useVoiceDictation } from "./voiceDictation";
import { inspectFile } from "./fileDownloads";

import { useComposerDraftField } from "./useComposerDraftField";
import { useMediaQuery } from "../ui/useMediaQuery";

type Props = {
  conversation: Conversation;
  mentionables: readonly Participant[];
  placeholder: string;
  threadRootId?: string;
  compact?: boolean;
  autoFocus?: boolean;
};

const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const MAX_OTHER_FILE_BYTES = 25 * 1024 * 1024;

export function Composer({ conversation, mentionables, placeholder, threadRootId, compact = false, autoFocus = false }: Props) {
  const sendMessage = useMutation(api.communication.sendMessage);
  const createUploadUrl = useMutation(api.communication.createUploadUrl);
  const scheduleAnnouncement = useMutation(api.communication.scheduleAnnouncement);
  const draftKey = JSON.stringify([conversation.id, threadRootId ?? null]);
  const phoneKeyboard = useMediaQuery("(pointer: coarse)");
  const [draft, setDraft] = useComposerDraftField(draftKey, "draft");
  const [files, setFiles] = useComposerDraftField(draftKey, "files");
  const [sending, setSending] = useComposerDraftField(draftKey, "sending");
  const [error, setError] = useComposerDraftField(draftKey, "error");
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useComposerDraftField(draftKey, "scheduleOpen");
  const [scheduledFor, setScheduledFor] = useComposerDraftField(draftKey, "scheduledFor");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const readOnly = conversation.channelRole === "viewer";
  const addTranscript = useCallback((text: string) => {
    setDraft((current) => appendTranscript(current, text));
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [setDraft]);
  const dictation = useVoiceDictation({ conversationId: conversation.id, disabled: readOnly, onTranscript: addTranscript });

  useEffect(() => { if (autoFocus) textareaRef.current?.focus(); }, [autoFocus]);
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 180)}px`;
  }, [draft]);

  const suggestions = mentionQuery === null ? [] : mentionables.filter((participant) => participant.displayName.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6);

  const updateDraft = (value: string, caret: number) => {
    setDraft(value);
    const before = value.slice(0, caret);
    const match = /(?:^|\s)@([^\s@]{0,30})$/.exec(before);
    setMentionQuery(match === null ? null : match[1] ?? "");
    setMentionIndex(0);
  };

  const insertMention = (participant: Participant) => {
    const textarea = textareaRef.current;
    const caret = textarea?.selectionStart ?? draft.length;
    const before = draft.slice(0, caret).replace(/@[^\s@]{0,30}$/, "");
    const after = draft.slice(caret);
    const next = `${before}@${participant.displayName} ${after}`;
    setDraft(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const position = before.length + participant.displayName.length + 2;
      textarea?.setSelectionRange(position, position);
      textarea?.focus();
    });
  };

  const attach = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = [...(event.target.files ?? [])];
    event.target.value = "";
    for (const file of chosen) {
      const key = `${file.name}-${file.size}-${Date.now()}`;
      setFiles((current) => [...current, { key, file }]);
      try {
        const inspected = await inspectFile(file, file.name, file.type);
        const isImage = inspected.mediaType.startsWith("image/");
        if (file.size > (isImage ? MAX_IMAGE_BYTES : MAX_OTHER_FILE_BYTES)) {
          throw new Error(isImage ? "Image is larger than 50 MB" : "File is larger than 25 MB");
        }
        const upload = new File([inspected.blob], inspected.filename, { type: inspected.mediaType, lastModified: file.lastModified });
        setFiles((current) => current.map((entry) => entry.key === key ? { ...entry, file: upload } : entry));
        const url = await createUploadUrl({ conversationId: conversation.id });
        const response = await fetch(url, { method: "POST", headers: { "Content-Type": upload.type }, body: upload });
        if (!response.ok) throw new Error(`Upload failed (${response.status})`);
        const { storageId } = await response.json() as { storageId: string };
        setFiles((current) => current.map((entry) => entry.key === key ? { ...entry, storageId } : entry));
      } catch (caught) {
        setFiles((current) => current.map((entry) => entry.key === key ? { ...entry, error: errorMessage(caught) } : entry));
      }
    }
  };

  const submit = async () => {
    const plainText = draft.trim();
    const ready = files.filter((entry) => entry.storageId !== undefined);
    if ((plainText.length === 0 && ready.length === 0) || sending) return;
    if (dictation.phase !== "idle") { setError("Wait for voice transcription to finish before sending."); return; }
    if (files.some((entry) => entry.storageId === undefined && entry.error === undefined)) { setError("Still uploading — one moment."); return; }
    setSending(true);
    setError(null);
    try {
      if (scheduleOpen) {
        if (ready.length > 0) throw new Error("Scheduled announcements cannot include attachments yet.");
        const time = new Date(scheduledFor).getTime();
        if (!Number.isFinite(time)) throw new Error("Choose when to publish this announcement.");
        await scheduleAnnouncement({ conversationId: conversation.id, plainText, scheduledFor: time });
      } else {
        const mentionPrincipalIds = mentionables.filter((participant) => plainText.includes(`@${participant.displayName}`)).map((participant) => participant.principalId);
        await sendMessage({ conversationId: conversation.id, ...(threadRootId === undefined ? {} : { threadRootId }), plainText, idempotencyKey: idempotencyKey(), mentionPrincipalIds, attachments: ready.map((entry) => ({ storageId: entry.storageId ?? "", filename: entry.file.name, mediaType: entry.file.type || "application/octet-stream", size: entry.file.size })) });
      }
      setDraft("");
      setFiles([]);
      setScheduleOpen(false); setScheduledFor("");
      setMentionQuery(null);
      textareaRef.current?.focus();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (mentionQuery !== null && suggestions.length > 0) {
      if (event.key === "ArrowDown") { event.preventDefault(); setMentionIndex((current) => (current + 1) % suggestions.length); return; }
      if (event.key === "ArrowUp") { event.preventDefault(); setMentionIndex((current) => (current - 1 + suggestions.length) % suggestions.length); return; }
      if (event.key === "Enter" || event.key === "Tab") { event.preventDefault(); const pick = suggestions[mentionIndex]; if (pick !== undefined) insertMention(pick); return; }
      if (event.key === "Escape") { event.preventDefault(); setMentionQuery(null); return; }
    }
    if (event.key === "Enter" && !event.shiftKey && (!phoneKeyboard || event.metaKey || event.ctrlKey)) { event.preventDefault(); void submit(); }
  };

  const canSend = !sending && dictation.phase === "idle" && (draft.trim().length > 0 || files.some((entry) => entry.storageId !== undefined));

  return (
    <form className={compact ? "composer composer--compact" : "composer"} onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      {readOnly ? <p className="composer__read-only">You have view-only access in this channel.</p> : null}
      {mentionQuery !== null && suggestions.length > 0 ? (
        <ul className="mention-popover" role="listbox" aria-label="Mention someone">
          {suggestions.map((participant, index) => (
            <li key={participant.principalId}>
              <button type="button" role="option" aria-selected={index === mentionIndex} className={index === mentionIndex ? "is-active" : undefined} onMouseDown={(event) => { event.preventDefault(); insertMention(participant); }}>
                {participant.kind === "openclaw" ? <Bot size={13} /> : <AtSign size={13} />}<span>{participant.displayName}</span>{participant.kind === "openclaw" ? <small>AI teammate</small> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {files.length > 0 ? (
        <div className="file-chips">
          {files.map((entry) => (
            <span key={entry.key} className={entry.error !== undefined ? "file-chip file-chip--error" : entry.storageId === undefined ? "file-chip file-chip--pending" : "file-chip"} title={entry.error}>
              <Paperclip size={11} /> {entry.file.name} <small>{entry.error ?? (entry.storageId === undefined ? "uploading…" : formatBytes(entry.file.size))}</small>
              <button type="button" aria-label={`Remove ${entry.file.name}`} onClick={() => { setFiles((current) => current.filter((candidate) => candidate.key !== entry.key)); }}><X size={11} /></button>
            </span>
          ))}
        </div>
      ) : null}
      {error !== null || dictation.error !== null ? <p className="composer-status form-error" role="alert">{error ?? dictation.error}</p> : null}
      <textarea
        ref={textareaRef}
        name={threadRootId === undefined ? "message" : "thread-reply"}
        aria-label={placeholder}
        placeholder={placeholder}
        rows={compact ? 2 : 2}
        value={draft}
        disabled={readOnly || sending}
        onChange={(event) => { updateDraft(event.target.value, event.target.selectionStart); }}
        onKeyDown={onKeyDown}
        onBlur={() => { window.setTimeout(() => { setMentionQuery(null); }, 120); }}
      />
      <div className="composer__toolbar">
        <div>
          <input ref={fileInputRef} name="attachments" type="file" multiple hidden onChange={(event) => { void attach(event); }} />
          <Button size="icon" variant="ghost" aria-label="Attach file" title="Attach file" disabled={readOnly} onClick={() => { fileInputRef.current?.click(); }}><Paperclip size={16} /></Button>
          <Button size="icon" variant="ghost" aria-label="Mention someone" title="Mention someone" disabled={readOnly} onClick={() => { const next = draft.length === 0 || draft.endsWith(" ") ? `${draft}@` : `${draft} @`; setDraft(next); setMentionQuery(""); textareaRef.current?.focus(); }}><AtSign size={16} /></Button>
          <Button
            size="icon"
            variant="ghost"
            className={dictation.phase === "recording" ? "dictation-button is-recording" : "dictation-button"}
            aria-label={dictation.phase === "recording" ? "Stop voice dictation" : "Start voice dictation"}
            title={dictation.phase === "recording" ? "Stop recording" : "Dictate with private MacBook transcription — no patient information"}
            disabled={readOnly || !dictation.supported || dictation.phase === "uploading" || dictation.phase === "transcribing"}
            onClick={() => { if (dictation.phase === "recording") dictation.stop(); else void dictation.start(); }}
          >
            {dictation.phase === "recording" ? <Square size={14} fill="currentColor" /> : dictation.phase === "uploading" || dictation.phase === "transcribing" ? <LoaderCircle className="dictation-spinner" size={16} /> : <Mic size={16} />}
          </Button>
          {conversation.type === "announcement" && threadRootId === undefined ? <Button size="icon" variant="ghost" aria-label="Schedule announcement" title="Schedule announcement" disabled={readOnly} onClick={() => { setScheduleOpen((current) => !current); if (!scheduledFor) { const next = new Date(Date.now() + 60 * 60 * 1000); setScheduledFor(new Date(next.getTime() - next.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)); } }}><CalendarClock size={16} /></Button> : null}
        </div>
        <div className="send-group">
          {dictation.phase === "recording" ? <span className="dictation-status" role="status">Listening… {formatRecordingTime(dictation.seconds)}</span> : dictation.phase === "uploading" ? <span className="dictation-status" role="status">Securing recording…</span> : dictation.phase === "transcribing" ? <span className="dictation-status" role="status">Transcribing on your MacBook…</span> : <span className="composer-key-hint">{phoneKeyboard ? "Return for a new line" : <><kbd>↵</kbd> to send · <kbd>⇧↵</kbd> new line</>}</span>}
          <Button type="submit" size="icon" variant="primary" aria-label="Send message" disabled={!canSend || readOnly}><Send size={15} /></Button>
        </div>
      </div>
      {scheduleOpen ? <div className="schedule-row"><label>Publish at<input type="datetime-local" value={scheduledFor} min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)} onChange={(event) => { setScheduledFor(event.target.value); }} /></label><span>This posts automatically and sends the same private notification as a normal announcement.</span></div> : null}
    </form>
  );
}
